import { Clock, Effect, Equal, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { Rule, makeRules, validate } from "foldkit/fieldValidation";
import * as Navigation from "foldkit/navigation";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";
import * as Relationships from "../../widgets/relationships";

import { settle, start } from "../../api/asyncData";
import { createEntries } from "../../api/commands/addEntries";
import { getEntries } from "../../api/commands/entries";
import { setCadence } from "../../api/commands/keepInTouch";
import { getBacklinks } from "../../api/commands/notes";
import {
	checkProfileImageExists,
	editRecord,
	getRecord,
	getRecordHistory,
	setRecordAgentLink,
	uploadProfileImage,
} from "../../api/commands/records";
import { listTasks } from "../../api/commands/tasks";
import { type ApiError, toMessage } from "../../api/errors";
import type { Entry } from "../../api/schemas/entries";
import type { Cadence, RecordSummary } from "../../api/schemas/records";
import { makeTrack } from "../../app/analytics";
import { Route, asOfOf, toHref } from "../../app/routes";
import { i18next } from "../../i18n";
import * as AddEntry from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import { Message as CropMessage } from "../../widgets/cropper";
import { SetEditorText } from "../../widgets/editor";
import * as Tags from "../../widgets/tags";
import { RECORD_FIELD_MAX } from "../../widgets/validatedField";
import { nextCursor, settlePage } from "../history";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import {
	type EditForm,
	type Entries,
	type ImageModal,
	ImageStage,
	type Model,
	emptyImageModal,
} from "./model";

// COMMANDS

export const Track = makeTrack(Message.CompletedTrack);

/** KTD8: the visit came from a keep-in-touch card's Record link. */
const fromCard = (route: Route): boolean =>
	route._tag === "Record" &&
	Option.contains(route.via ?? Option.none(), "keep-in-touch");

const nullable = (asOf: Option.Option<string>) => Option.getOrNull(asOf);

export const FetchRecord = getRecord.command(
	Message.CompletedFetchRecord,
	({ nodeId, asOf, generation, result }) =>
		Message.CompletedFetchRecord({
			nodeId,
			asOf: Option.fromNullishOr(asOf),
			generation,
			result,
		}),
);
export const FetchEntries = getEntries.command(
	Message.CompletedFetchEntries,
	({ nodeId, cursor, asOf, generation, result }) =>
		Message.CompletedFetchEntries({
			nodeId,
			cursor,
			asOf: Option.fromNullishOr(asOf),
			generation,
			result,
		}),
);
export const CheckImage = checkProfileImageExists.command(
	Message.CompletedCheckImage,
	({ nodeId, generation, result }) =>
		Message.CompletedCheckImage({ nodeId, generation, result }),
);
export const FetchHistory = getRecordHistory.command(
	Message.CompletedFetchHistory,
	({ nodeId, cursor, generation, result }) =>
		Message.CompletedFetchHistory({ nodeId, cursor, generation, result }),
);
export const EditRecord = editRecord.command(
	Message.CompletedEditRecord,
	({ generation, result }) =>
		Message.CompletedEditRecord({ generation, result }),
);
/** Completions carry the record id so the page's stale-id guard applies. */
export const SetCadence = setCadence.command(
	Message.CompletedSetCadence,
	({ recordId, generation, result }) =>
		Message.CompletedSetCadence({ nodeId: recordId, generation, result }),
);
/** L5: the record id travels as `nodeId`, so the stale-id guard applies. */
export const SetAgentLink = setRecordAgentLink.command(
	Message.CompletedSetAgentLink,
	({ nodeId, generation, result }) =>
		Message.CompletedSetAgentLink({ nodeId, generation, result }),
);
export const UploadImage = uploadProfileImage.command(
	Message.CompletedUpload,
	({ generation, result }) => Message.CompletedUpload({ generation, result }),
);

/** U12: the record id travels as `person`, so the stale-id guard applies. */
export const FetchPersonTasks = Command.define("FetchPersonTasks", {
	args: { person: S.String, generation: S.Number },
	messages: [Message.CompletedFetchPersonTasks],
	execute: ({ person, generation }) =>
		Effect.all([
			Clock.currentTimeMillis,
			listTasks.run({
				query: "",
				statuses: [],
				priorities: [],
				tags: [],
				graded: "all",
				sort: "attention",
				cursor: null,
				people: [person],
			}),
		]).pipe(
			Effect.map(([now, result]) =>
				Message.CompletedFetchPersonTasks({
					nodeId: person,
					generation,
					now,
					result,
				}),
			),
		),
});
export const FetchBacklinks = getBacklinks.command(
	Message.CompletedFetchBacklinks,
	({ target, generation, result }) =>
		Message.CompletedFetchBacklinks({ nodeId: target, generation, result }),
);

/** v1 `HistoryVersionCard` "View at this point": time-travels to that version. */
export const NavigateTo = Command.define("NavigateToRecord", {
	args: { url: S.String },
	messages: [Message.CompletedNavigate],
	execute: ({ url }) =>
		Navigation.pushUrl(url).pipe(Effect.as(Message.CompletedNavigate())),
});

/** KTD2: the write itself is shared; only the completion message is page-specific. */
export const AddEntries = Command.define("AddEntries", {
	args: { nodeId: S.String, request: AddEntry.Request, generation: S.Number },
	messages: [Message.CompletedAddEntries],
	execute: ({ nodeId, request, generation }) =>
		createEntries(nodeId, request).pipe(
			Effect.map((result) =>
				Message.CompletedAddEntries({ nodeId, generation, result }),
			),
		),
});

// RULES (v1 react-hook-form registrations)

export const nameRules = makeRules({
	required: "This field is required",
	isEmpty: (value) => value.trim() === "",
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.name,
			`Name must be ${RECORD_FIELD_MAX.name} characters or fewer`,
		),
	],
});
export const subtitleRules = makeRules({
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.subtitle,
			`Subtitle must be ${RECORD_FIELD_MAX.subtitle} characters or fewer`,
		),
	],
});
export const emailRules = makeRules({
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.email,
			`Email must be ${RECORD_FIELD_MAX.email} characters or fewer`,
		),
	],
});
export const descriptionRules = makeRules({
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.description,
			`Description must be ${RECORD_FIELD_MAX.description} characters or fewer`,
		),
	],
});

// HELPERS

type Return = PageReturn<Model, Message>;
type Step = (model: Model) => Return;

export const initial = (nodeId: string): Model => ({
	page: "Record",
	relationships: Relationships.init(nodeId),
	nodeId,
	record: AsyncData.Idle(),
	entries: AsyncData.Idle(),
	image: AsyncData.Idle(),
	tab: "entries",
	history: AsyncData.Idle(),
	expandedHistory: [],
	edit: Option.none(),
	saving: false,
	imageModal: Option.none(),
	addEntry: AddEntry.init(),
	cadenceError: Option.none(),
	agentLinkDraft: Option.none(),
	agentLinkError: Option.none(),
	tasks: AsyncData.Idle(),
	tasksReadAt: 0,
	backlinks: AsyncData.Idle(),
});

const emptyEntries: Entries = { items: [], nextCursor: Option.none() };
export const entryItems = (model: Model): ReadonlyArray<Entry> =>
	AsyncData.getOrElse(model.entries, () => emptyEntries).items;

/** v1 `resolveSubtitle`: the `subtitle` attribute wins over the column. */
export const resolveSubtitle = (record: RecordSummary): string =>
	record.attributes?.subtitle?.trim() || record.subtitle?.trim() || "";

/** v1's three error texts; `undefined` when the record is not in a failed state. */
export const errorText = (model: Model, shared: Shared): string | undefined => {
	if (!AsyncData.isFailure(model.record)) return undefined;
	const error = model.record.error;
	if (error._tag !== "NotFound") return i18next.t("record.failedToLoad");
	return Option.match(shared.asOf, {
		onNone: () => i18next.t("record.notFound"),
		onSome: (asOf) =>
			i18next.t("record.notFoundAtDate", {
				date: new Date(asOf).toLocaleDateString(),
			}),
	});
};

const fetchEntries = (
	model: Model,
	cursor: number,
	shared: Shared,
): Return => ({
	model: evo(model, { entries: start }),
	commands: [
		FetchEntries({
			nodeId: model.nodeId,
			search: "",
			cursor,
			asOf: nullable(shared.asOf),
			generation: shared.generation,
		}),
	],
});

const fetchRecord = (model: Model, shared: Shared): Return => ({
	model: evo(model, { record: start }),
	commands: [
		FetchRecord({
			nodeId: model.nodeId,
			asOf: nullable(shared.asOf),
			generation: shared.generation,
		}),
	],
});

const checkImage = (model: Model, shared: Shared): Return => ({
	model: evo(model, { image: start }),
	commands: [
		CheckImage({ nodeId: model.nodeId, generation: shared.generation }),
	],
});

const fetchHistory = (
	model: Model,
	cursor: number,
	shared: Shared,
): Return => ({
	model: evo(model, { history: start }),
	commands: [
		FetchHistory({
			nodeId: model.nodeId,
			cursor,
			generation: shared.generation,
		}),
	],
});

/** U12: Tasks and Notes are not time-versioned, so they fetch without `asOf`. */
const fetchPersonTasks = (model: Model, shared: Shared): Return => ({
	model: evo(model, { tasks: start }),
	commands: [
		FetchPersonTasks({ person: model.nodeId, generation: shared.generation }),
	],
});

const fetchBacklinks = (model: Model, shared: Shared): Return => ({
	model: evo(model, { backlinks: start }),
	commands: [
		FetchBacklinks({ target: model.nodeId, generation: shared.generation }),
	],
});

/** U12: counts sit in the tab labels, so both load once a person and the flag are both in. */
const loadPersonTabs =
	(shared: Shared): Step =>
	(model) => {
		const isPerson =
			AsyncData.getOrElse(model.record, () => null)?.type === "person";
		if (!(shared.a2aContextEnabled && isPerson)) return { model };
		return andThen(
			(m) =>
				AsyncData.isIdle(m.tasks) ? fetchPersonTasks(m, shared) : { model: m },
			(m) =>
				AsyncData.isIdle(m.backlinks)
					? fetchBacklinks(m, shared)
					: { model: m },
		)(model);
	};

const andThen =
	(first: Step, next: Step): Step =>
	(model) => {
		const a = first(model);
		const b = next(a.model);
		return {
			model: b.model,
			commands: [...(a.commands ?? []), ...(b.commands ?? [])],
		};
	};

const load = (shared: Shared): Step =>
	andThen(
		andThen(
			(m) => fetchRecord(m, shared),
			(m) => fetchEntries(m, 0, shared),
		),
		(m) => checkImage(m, shared),
	);

const editForm = (model: Model, f: (form: EditForm) => EditForm): Model =>
	evo(model, { edit: (edit) => Option.map(edit, f) });

const imageModal = (
	model: Model,
	f: (modal: ImageModal) => ImageModal,
): Model => evo(model, { imageModal: (modal) => Option.map(modal, f) });

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

export const recordSchema = (shared: Shared) =>
	Attributes.schemaFor(shared, "record");

const foldTags = Update.foldChild({
	update: (child: Tags.Model, message: Tags.Message) =>
		Tags.update(child, message),
	read: (model: Model) => Option.map(model.edit, (form) => form.tagBox),
	write: (model: Model, tagBox: Tags.Model) =>
		editForm(model, (form) => ({ ...form, tagBox })),
	toParentMessage: (message: Tags.Message) =>
		Message.GotTagsMessage({ message }),
	foldOutMessage:
		(out: Tags.OutMessage) =>
		(model: Model): Return =>
			Tags.OutMessage.match(out, {
				// v1 `onAdd`: invalid labels show the rule and add nothing.
				Selected: ({ value }) => {
					const tag = Tags.sanitizeTag(value);
					return {
						model: editForm(model, (form) =>
							tag === undefined
								? {
										...form,
										tagError: Option.some(Tags.TAG_VALIDATION_MESSAGE),
									}
								: {
										...form,
										tagError: Option.none(),
										tags: [...form.tags, tag],
									},
						),
					};
				},
				ClearedSelection: () => ({ model }),
			}),
});

/** Built per fold so the Submitted fact can carry the current generation. */
const foldAddEntry = (shared: Shared) =>
	Update.foldChild({
		update: (child: AddEntry.Model, message: AddEntry.Message) =>
			AddEntry.update(child, message),
		read: (model: Model) => Option.some(model.addEntry),
		write: (model: Model, addEntry: AddEntry.Model) =>
			evo(model, { addEntry: () => addEntry }),
		toParentMessage: (message: AddEntry.Message) =>
			Message.GotAddEntryMessage({ message }),
		foldOutMessage:
			(out: AddEntry.OutMessage) =>
			(model: Model): Return =>
				AddEntry.OutMessage.match(out, {
					Submitted: ({ request }) => ({
						model,
						commands: [
							AddEntries({
								nodeId: model.nodeId,
								request,
								generation: shared.generation,
							}),
						],
					}),
				}),
	});

const submitEdit = (
	model: Model,
	form: EditForm,
	record: RecordSummary,
	shared: Shared,
): Return => {
	const name = validate(nameRules)(form.name.value);
	const subtitle = validate(subtitleRules)(form.subtitle.value);
	const email = validate(emailRules)(form.email.value);
	const description = validate(descriptionRules)(form.description.value);
	const validated = editForm(model, (f) => ({
		...f,
		name,
		subtitle,
		email,
		description,
	}));
	if ([name, subtitle, email, description].some((f) => f._tag === "Invalid")) {
		return { model: validated };
	}
	// v1: reserved keys merge into the attributes map ahead of the editor's rows.
	const trimmedSubtitle = subtitle.value.trim();
	const custom = Attributes.flattenRows(form.attributes) ?? {};
	const attributes = {
		...(trimmedSubtitle ? { subtitle: trimmedSubtitle } : {}),
		...(email.value ? { email: email.value } : {}),
		...custom,
	};
	return {
		model: evo(validated, { saving: () => true }),
		commands: [
			EditRecord({
				nodeId: model.nodeId,
				record: {
					...record,
					name: name.value,
					...(trimmedSubtitle ? { subtitle: trimmedSubtitle } : {}),
					description: description.value,
					tags: Array.from(new Set(form.tags)),
					attributes,
				},
				generation: shared.generation,
			}),
		],
	};
};

/** U8: writes are now-only, so nothing is issued under time travel. */
const setCadenceWrite = (
	model: Model,
	shared: Shared,
	fields: (record: RecordSummary) => {
		cadence: Cadence | null;
		paused: boolean;
	},
): Return => {
	const record = AsyncData.getOrElse(model.record, () => null);
	if (record === null || Option.isSome(shared.asOf)) return { model };
	return {
		model: evo(model, { cadenceError: () => Option.none() }),
		commands: [
			SetCadence({
				recordId: model.nodeId,
				...fields(record),
				generation: shared.generation,
			}),
		],
	};
};

/** L5: the stored agent link (a reserved attribute), or "". */
export const storedAgentLink = (record: RecordSummary): string =>
	record.attributes?.agentLink ?? "";

/** L5: like cadence, a now-only write on a person; `null` clears. */
const setAgentLinkWrite = (
	model: Model,
	shared: Shared,
	url: (record: RecordSummary) => string | null,
): Return => {
	const record = AsyncData.getOrElse(model.record, () => null);
	if (record === null || record.type !== "person" || Option.isSome(shared.asOf))
		return { model };
	return {
		model: evo(model, { agentLinkError: () => Option.none() }),
		commands: [
			SetAgentLink({
				nodeId: model.nodeId,
				url: url(record),
				generation: shared.generation,
			}),
		],
	};
};

const relationshipSlot = {
	read: (model: Model) => Option.some(model.relationships),
	write: (model: Model, relationships: Relationships.Model): Model => ({
		...model,
		relationships,
	}),
	toParentMessage: (message: Relationships.Message) =>
		Message.GotRelationshipsMessage({ message }),
	foldOutMessage:
		() =>
		(model: Model): Return => ({ model }),
};
const foldRelationships = (shared: Shared) =>
	Update.foldChild({
		...relationshipSlot,
		update: (child: Relationships.Model, message: Relationships.Message) =>
			Relationships.update(child, message, shared),
	});
const loadRelationships = (shared: Shared) =>
	Update.foldChildStep({
		...relationshipSlot,
		update: (child: Relationships.Model) => Relationships.reload(child, shared),
	});
const clearRelationships = Update.foldChildStep({
	...relationshipSlot,
	update: (child: Relationships.Model) => ({
		model: { ...Relationships.init(child.ownerId), epoch: child.epoch + 1 },
	}),
});

// PAGE

export const page: Page<Model, Message> = {
	init: (route, shared) =>
		load(shared)(initial(route._tag === "Record" ? route.id : "")),
	changedRoute: (model, previous, next, shared) => {
		const nextId = next._tag === "Record" ? next.id : model.nodeId;
		if (nextId !== model.nodeId) return load(shared)(initial(nextId));
		return Equal.equals(asOfOf(previous), asOfOf(next))
			? { model }
			: andThen(
					(m) => fetchRecord(m, shared),
					(m) => fetchEntries(m, 0, shared),
				)(clearRelationships(model).model);
	},
	// A completion tagged with another record's id is stale: the route moved on.
	update: (model, message, shared) =>
		"nodeId" in message && message.nodeId !== model.nodeId
			? { model }
			: updateOwn(model, message, shared),
};

const updateOwn = (model: Model, message: Message, shared: Shared): Return =>
	Message.match<Return>(message, {
		InteractionLogged: ({ nodeId }) =>
			nodeId === model.nodeId && Option.isNone(shared.asOf)
				? fetchEntries(model, 0, shared)
				: { model },
		GotRelationshipsMessage: ({ message }) =>
			foldRelationships(shared)(model, message),
		CompletedFetchRecord: ({ asOf, generation, result }) => {
			if (generation !== shared.generation || !Equal.equals(asOf, shared.asOf))
				return { model };
			const updated = { ...model, record: settle(model.record, result) };
			if (!(Result.isSuccess(result) && result.success.type === "person"))
				return { model: updated };
			return andThen(
				loadRelationships(shared),
				loadPersonTabs(shared),
			)(updated);
		},
		// The a2a-context flag can settle after the record: load the counts then.
		ChangedShared: () => loadPersonTabs(shared)(model),
		CompletedFetchEntries: ({ cursor, asOf, generation, result }) => {
			if (
				generation !== shared.generation ||
				!Equal.equals(asOf, shared.asOf)
			) {
				return { model };
			}
			return {
				model: evo(model, {
					entries: (entries) => settlePage(entries, cursor, result),
				}),
			};
		},
		CompletedCheckImage: ({ generation, result }) => ({
			model:
				generation === shared.generation
					? evo(model, { image: (image) => settle(image, result) })
					: model,
		}),
		ClickedLoadMoreEntries: () =>
			Option.match(nextCursor(model.entries), {
				onNone: () => ({ model }),
				onSome: (cursor) => fetchEntries(model, cursor, shared),
			}),
		// v1 fetches history when the tab first renders and keeps it cached.
		SelectedTab: ({ tab }) => {
			const switched = evo(model, { tab: () => tab });
			if (tab === "history" && AsyncData.isIdle(model.history))
				return fetchHistory(switched, 0, shared);
			if (tab === "tasks" && AsyncData.isIdle(model.tasks))
				return fetchPersonTasks(switched, shared);
			if (tab === "notes" && AsyncData.isIdle(model.backlinks))
				return fetchBacklinks(switched, shared);
			return { model: switched };
		},
		CompletedFetchPersonTasks: ({ generation, now, result }) => ({
			model:
				generation === shared.generation
					? evo(model, {
							tasks: (tasks) => settle(tasks, result),
							tasksReadAt: () => now,
						})
					: model,
		}),
		CompletedFetchBacklinks: ({ generation, result }) => ({
			model:
				generation === shared.generation
					? evo(model, {
							backlinks: (backlinks) => settle(backlinks, result),
						})
					: model,
		}),
		CompletedFetchHistory: ({ cursor, generation, result }) => {
			if (generation !== shared.generation) return { model };
			return {
				model: evo(model, {
					history: (history) => settlePage(history, cursor, result),
				}),
			};
		},
		ToggledHistoryCard: ({ id }) => ({
			model: evo(model, {
				expandedHistory: (ids) =>
					ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
			}),
		}),
		ClickedLoadMoreHistory: () =>
			Option.match(nextCursor(model.history), {
				onNone: () => ({ model }),
				onSome: (cursor) => fetchHistory(model, cursor, shared),
			}),
		ClickedViewAt: ({ validFrom }) => ({
			model,
			commands: [
				NavigateTo({
					url: toHref(
						Route.Record({ id: model.nodeId, asOf: Option.some(validFrom) }),
					),
				}),
			],
		}),
		CompletedNavigate: () => ({ model }),
		// v1 seeds the form with the resolved subtitle, the email attribute, and the tags.
		ClickedEdit: () => {
			const record = AsyncData.getOrElse(model.record, () => null);
			if (record === null || Option.isSome(shared.asOf)) return { model };
			const notValidated = (value: string) =>
				({ _tag: "NotValidated", value }) as const;
			return {
				model: evo(model, {
					edit: () =>
						Option.some<EditForm>({
							name: notValidated(record.name),
							subtitle: notValidated(resolveSubtitle(record)),
							email: notValidated(record.attributes?.email ?? ""),
							description: notValidated(record.description ?? ""),
							tags: record.tags
								.map((tag) => Tags.sanitizeTag(tag))
								.filter((tag): tag is string => tag !== undefined),
							tagBox: Tags.init("edit-record-tags"),
							tagError: Option.none(),
							attributes: Attributes.buildRows(
								record.attributes,
								recordSchema(shared),
							),
							error: Option.none(),
						}),
				}),
			};
		},
		ChangedName: ({ value }) => ({
			model: editForm(model, (f) => ({
				...f,
				name: { _tag: "NotValidated", value },
			})),
		}),
		ChangedSubtitle: ({ value }) => ({
			model: editForm(model, (f) => ({
				...f,
				subtitle: { _tag: "NotValidated", value },
			})),
		}),
		ChangedEmail: ({ value }) => ({
			model: editForm(model, (f) => ({
				...f,
				email: { _tag: "NotValidated", value },
			})),
		}),
		ChangedDescription: ({ value }) => ({
			model: editForm(model, (f) => ({
				...f,
				description: { _tag: "NotValidated", value },
			})),
		}),
		GotTagsMessage: ({ message }) => foldTags(model, message),
		ClickedRemoveTag: ({ tag }) => ({
			model: editForm(model, (f) => ({
				...f,
				tagError: Option.none(),
				tags: f.tags.filter((t) => t !== tag),
			})),
		}),
		GotAttributesMessage: ({ message }) => ({
			model: editForm(model, (f) => ({
				...f,
				attributes: Attributes.update(
					f.attributes,
					message,
					recordSchema(shared),
				),
			})),
		}),
		ClickedDismissError: () => ({
			model: editForm(model, (f) => ({ ...f, error: Option.none() })),
		}),
		SubmittedEdit: () => {
			const record = AsyncData.getOrElse(model.record, () => null);
			return Option.match(model.edit, {
				onNone: () => ({ model }),
				onSome: (form) =>
					model.saving || record === null
						? { model }
						: submitEdit(model, form, record, shared),
			});
		},
		CompletedEditRecord: ({ generation, result }) => {
			if (generation !== shared.generation) return { model };
			const settled = evo(model, { saving: () => false });
			return Result.match(result, {
				// v1: invalidates the record, the list, and search, and closes edit mode.
				onSuccess: () =>
					fetchRecord(
						evo(settled, {
							edit: () => Option.none(),
							imageModal: () => Option.none(),
						}),
						shared,
					),
				onFailure: (error) => ({
					model: editForm(settled, (f) => ({
						...f,
						error: Option.some(toMessage(error)),
					})),
				}),
			});
		},
		// v1: the modal is reachable only in edit mode.
		ClickedChangeImage: () => ({
			model: Option.isSome(model.edit)
				? evo(model, { imageModal: () => Option.some(emptyImageModal) })
				: model,
		}),
		ClickedCloseImageModal: () => ({
			model: evo(model, { imageModal: () => Option.none() }),
		}),
		ChangedImageFile: ({ file }) => ({
			model: imageModal(model, (modal) =>
				Option.match(file, {
					onNone: () => ({ ...modal, stage: ImageStage.Pick() }),
					onSome: (f) =>
						f.type === "image/gif"
							? {
									...modal,
									stage: ImageStage.Pick(),
									error: Option.some(
										"GIF files are not supported. Please upload a JPEG or PNG image.",
									),
								}
							: f.size > MAX_IMAGE_BYTES
								? {
										...modal,
										stage: ImageStage.Pick(),
										error: Option.some(
											"Image size must be less than 2MB. Please choose a smaller image.",
										),
									}
								: {
										...modal,
										stage: ImageStage.Crop({ file: f }),
										error: Option.none(),
										uploaded: false,
									},
				}),
			),
		}),
		GotCropMessage: ({ message }) =>
			CropMessage.match<Return>(message, {
				CroppedImage: ({ blob }) => ({
					model: imageModal(model, (modal) => ({
						...modal,
						stage: ImageStage.Preview({ blob }),
					})),
				}),
				CropCancelled: () => ({
					model: evo(model, { imageModal: () => Option.none() }),
				}),
			}),
		CompletedPreview: () => ({ model }),
		// v1's "Back to Crop" keeps the crop state; v2 re-picks the file (a recorded gap).
		ClickedBackToCrop: () => ({
			model: imageModal(model, (modal) => ({
				...modal,
				stage: ImageStage.Pick(),
				uploaded: false,
			})),
		}),
		ClickedUpload: () =>
			Option.match(model.imageModal, {
				onNone: () => ({ model }),
				onSome: (modal) =>
					modal.stage._tag !== "Preview" || modal.uploading || modal.uploaded
						? { model }
						: {
								model: imageModal(model, (m) => ({
									...m,
									uploading: true,
									error: Option.none(),
								})),
								commands: [
									UploadImage({
										nodeId: model.nodeId,
										image: modal.stage.blob,
										generation: shared.generation,
									}),
								],
							},
			}),
		CompletedUpload: ({ generation, result }) => {
			if (generation !== shared.generation) return { model };
			return Result.match(result, {
				onSuccess: () =>
					checkImage(
						imageModal(model, (m) => ({
							...m,
							uploading: false,
							uploaded: true,
						})),
						shared,
					),
				onFailure: (error) => ({
					model: imageModal(model, (m) => ({
						...m,
						uploading: false,
						error: Option.some(toMessage(error)),
					})),
				}),
			});
		},
		ChangedCadence: ({ cadence }) =>
			setCadenceWrite(model, shared, (record) => ({
				cadence,
				paused: record.paused ?? false,
			})),
		ClickedPause: () =>
			setCadenceWrite(model, shared, (record) => ({
				cadence: record.cadence ?? null,
				paused: !(record.paused ?? false),
			})),
		CompletedSetCadence: ({ generation, result }) =>
			generation !== shared.generation
				? { model }
				: Result.match(result, {
						onSuccess: () => fetchRecord(model, shared),
						onFailure: (error) => ({
							model: evo(model, {
								cadenceError: () => Option.some(toMessage(error)),
							}),
						}),
					}),
		ChangedAgentLink: ({ value }) => ({
			model: evo(model, {
				agentLinkDraft: () => Option.some(value),
				agentLinkError: () => Option.none(),
			}),
		}),
		// An emptied input saves as a clear.
		SubmittedAgentLink: () =>
			setAgentLinkWrite(model, shared, (record) => {
				const url = Option.getOrElse(model.agentLinkDraft, () =>
					storedAgentLink(record),
				).trim();
				return url === "" ? null : url;
			}),
		ClickedClearAgentLink: () => setAgentLinkWrite(model, shared, () => null),
		CompletedSetAgentLink: ({ generation, result }) =>
			generation !== shared.generation
				? { model }
				: Result.match(result, {
						onSuccess: () =>
							fetchRecord(
								evo(model, { agentLinkDraft: () => Option.none() }),
								shared,
							),
						// The server's reason is English; the page says it in the user's language.
						onFailure: () => ({
							model: evo(model, {
								agentLinkError: () =>
									Option.some(i18next.t("record.agentLink.invalid")),
							}),
						}),
					}),
		GotAddEntryMessage: ({ message }) => foldAddEntry(shared)(model, message),
		CompletedEditorAction: () => ({ model }),
		CompletedTrack: () => ({ model }),
		CompletedAddEntries: ({ generation, result }) => {
			if (generation !== shared.generation) return { model };
			return Result.match(result, {
				onSuccess: (created) => {
					const current = AsyncData.getOrElse(
						model.entries,
						() => emptyEntries,
					);
					const refetched = fetchEntries(
						evo(model, {
							addEntry: AddEntry.succeeded,
							entries: (entries) =>
								settle(
									entries,
									Result.succeed({
										...current,
										items: [...created, ...current.items],
									}),
								),
						}),
						0,
						shared,
					);
					// The form reset does not reach the mounted editor by itself (frozen Mount args).
					return {
						model: refetched.model,
						commands: [
							...(refetched.commands ?? []),
							SetEditorText({ id: AddEntry.EDITOR_ID, text: "" }),
							// KTD8: an entry recorded from a card's Record link.
							...(fromCard(shared.route)
								? [Track({ event: "entry_created_from_card", props: {} })]
								: []),
						],
					};
				},
				onFailure: (error) => ({
					model: evo(model, {
						addEntry: (form) => AddEntry.failed(form, toMessage(error)),
					}),
				}),
			});
		},
	});
